import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { useFlags } from 'launchdarkly-react-client-sdk';
import './intune-integration-card.css';
import { QCard, QCardContent, QTypography, QButton, QTextField, QDropdownWrapper } from '@kw/quokka-ui';
import { styled } from '@mui/material';
import { SettingsMode } from './enum/SettingsMode.enum';
import { httpRequest, sendHttpRequest } from '../../utils/network.service';
import { LaunchDarklyFlags } from '../../launch-darkly';
import { verifyAuthPermission } from '../../auth/VerifyAuthPermission';
import { AuthPermissions } from '../../auth/AuthPermissions';
import { KwFormErrorMessage } from '../../kw-ui-components/KwFormErrorMessage';

interface IntuneIntegrationCardProps {
  baseUrlPlaceholder: string;
  clientIdPlaceholder: string;
  clientSecretPlaceholder: string;
  tenantIdPlaceholder: string;
  lastSync: string;
  isAdmin: boolean;
  mode: { [key: string]: SettingsMode };
  setMode: React.Dispatch<React.SetStateAction<{ [key: string]: SettingsMode }>>;
  mutate: () => void;
}

export const IntuneIntegrationCard = ({
  baseUrlPlaceholder,
  clientIdPlaceholder,
  clientSecretPlaceholder,
  tenantIdPlaceholder,
  lastSync,
  isAdmin,
  mode,
  setMode,
  mutate,
}: IntuneIntegrationCardProps) => {
  const isEditMode = mode.intune === SettingsMode.EDIT;
  const isReadMode = mode.intune === SettingsMode.READ;
  const [baseUrl, setBaseUrl] = useState('https://graph.microsoft.com');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [tenantId, setTenantId] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [intuneFormErrors, setIntuneFormErrors] = useState({
    url: false,
    tenant: false,
    client: false,
    clientSecret: false,
    validated: false,
  });
  const [connectionFailed, setConnectionFailed] = useState(false);
  const lastSyncTimestamp = lastSync ? new Date(parseInt(lastSync, 10)).toLocaleString() : 'Never';

  const resetInputValues = () => {
    setClientId('');
    setClientSecret('');
    setTenantId('');
    setConnectionFailed(false);
    setIntuneFormErrors({ url: false, tenant: false, client: false, clientSecret: false, validated: false });
    setMode({ ...mode, intune: SettingsMode.READ });
  };

  function valdiateIntuneSettings() {
    setIntuneFormErrors({
      url: !baseUrl.length,
      tenant: !tenantId.length,
      client: !clientId.length,
      clientSecret: !clientSecret.length,
      validated: true,
    });
  }

  const updateIntuneSettings = async () => {
    valdiateIntuneSettings();

    if (!tenantId.length || !clientId.length || !clientSecret.length) {
      return;
    }

    const existingSettings = clientIdPlaceholder.length || clientSecretPlaceholder.length || tenantIdPlaceholder.length;
    let path = `${process.env.ORGANIZATION_SERVICE_URL}`;
    path += existingSettings ? '/organizations/mdm-integrations/1' : '/organizations/mdm-integrations';
    const method = existingSettings ? 'PUT' : 'POST';
    const body = existingSettings
      ? {}
      : { mdmIntegrationId: 1, settings: { base_url: baseUrl, client_id: clientId, client_secret: clientSecret, tenant_id: tenantId } };

    if (existingSettings) {
      const settings = {};

      Object.assign(settings, { base_url: baseUrl });

      if (clientId.length) {
        Object.assign(settings, { ...settings, client_id: clientId });
      }
      if (clientSecret.length) {
        Object.assign(settings, { ...settings, client_secret: clientSecret });
      }
      if (tenantId.length) {
        Object.assign(settings, { ...settings, tenant_id: tenantId });
      }

      Object.assign(body, { settings });
    }

    const connectionResult = await httpRequest({
      path: `${process.env.INTUNE_SERVICE_URL}/intune/check-connection`,
      method: 'POST',
      body: {
        baseUrl,
        clientId,
        clientSecret,
        tenantId,
      },
    });

    if (connectionResult.response.status !== 200) {
      setConnectionFailed(true);
      return;
    }

    await sendHttpRequest({
      path,
      method,
      body,
      successMessage: 'Intune settings updated successfully',
      errorMessage: 'Error updating Intune settings',
      enqueueSnackbar,
    });

    await syncNow();

    mutate();
    resetInputValues();
  };

  const syncNow = async () => {
    await sendHttpRequest({
      path: `${process.env.INTUNE_SERVICE_URL}/intune/sync`,
      method: 'POST',
      successMessage: 'Intune sync initiated successfully',
      errorMessage: 'Error initiating Intune sync',
      enqueueSnackbar,
    });
  };

  const flags: LaunchDarklyFlags['flags'] = useFlags();

  if (!flags?.intuneIntegration || !verifyAuthPermission(AuthPermissions.SETTINGS_INTEGRATIONS_MDM)) return null;

  const baseUrlOptions = [
    {
      value: 'https://graph.microsoft.com',
      label: 'Global: https://graph.microsoft.com',
    },
    {
      value: 'https://graph.microsoft.us',
      label: 'US Gov L4: https://graph.microsoft.us',
    },
    {
      value: 'https://dod-graph.microsoft.us',
      label: 'US Gov L5: https://dod-graph.microsoft.us',
    },
  ];

  return (
    <StyledQCard>
      <QCardContent>
        <StyledQTypography variant="h4Bold" gutterBottom>
          <img
            src="data:image/png;base64,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"
            className="intune-logo"
          />
          Microsoft Intune
        </StyledQTypography>
        <KwFormErrorMessage error={Boolean(connectionFailed)} reason="We couldn't make a connection with the information provided" />
        <StyledQTypography variant="bodyRegular" gutterBottom>
          Configure Q-scout&apos;s connection to the Graph API to import Managed and Discovered Mobile Apps from your Intune tenant.{' '}
          <a
            href="https://learn.microsoft.com/en-us/graph/api/resources/intune-graph-overview?view=graph-rest-1.0"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </a>
        </StyledQTypography>
        {isAdmin && isEditMode ? (
          <>
            <StyledQTypography variant="h5Bold">Base URL</StyledQTypography>
            <QDropdownWrapper
              selectedOption={baseUrl}
              closedLabel={baseUrlOptions.find(option => option.value === baseUrl)?.label ?? 'Select Base URL'}
              formControlProps={{ sx: { width: '100%' } }}
              onChange={e => setBaseUrl(e.target.value as string)}
              options={baseUrlOptions}
            />
            <StyledQTypography variant="h5Bold">Directory (tenant) ID</StyledQTypography>
            <QTextField
              placeholder={tenantIdPlaceholder}
              onChange={e => setTenantId(e.target.value)}
              error={Boolean(intuneFormErrors.tenant)}
            />
            <KwFormErrorMessage error={Boolean(intuneFormErrors.tenant)} reason="Enter a Directory (tenant) ID" />
            <StyledQTypography variant="h5Bold">Application (client) ID</StyledQTypography>
            <QTextField
              placeholder={clientIdPlaceholder}
              onChange={e => setClientId(e.target.value)}
              error={Boolean(intuneFormErrors.client)}
            />
            <KwFormErrorMessage error={Boolean(intuneFormErrors.client)} reason="Enter an Application (client) ID" />
            <StyledQTypography variant="h5Bold">Client Secret Value</StyledQTypography>
            <QTextField
              placeholder={clientSecretPlaceholder}
              onChange={e => setClientSecret(e.target.value)}
              error={Boolean(intuneFormErrors.clientSecret)}
            />
            <KwFormErrorMessage error={Boolean(intuneFormErrors.clientSecret)} reason="Enter a Client Secret Value" />
          </>
        ) : (
          <>
            <StyledQTypography variant="h5Bold">Base URL</StyledQTypography>
            <StyledQTypography>{baseUrlPlaceholder}</StyledQTypography>
            <StyledQTypography variant="h5Bold">Directory (tenant) ID</StyledQTypography>
            <StyledQTypography>{tenantIdPlaceholder}</StyledQTypography>
            <StyledQTypography variant="h5Bold">Application (client) ID</StyledQTypography>
            <StyledQTypography>{clientIdPlaceholder}</StyledQTypography>
            <StyledQTypography variant="h5Bold">Client Secret Value</StyledQTypography>
            <StyledQTypography>{clientSecretPlaceholder}</StyledQTypography>
          </>
        )}

        <div className="intune-action-wrapper">
          {isAdmin && isEditMode ? (
            <>
              <QButton onClick={resetInputValues}>Cancel</QButton>
              <QButton variant="filled" onClick={updateIntuneSettings}>
                Save
              </QButton>
            </>
          ) : (
            <>
              {isAdmin && isReadMode ? (
                <>
                  <div className="intune-sync-action-wrapper">
                    <QButton
                      style={{ padding: 0 }}
                      variant="text"
                      onClick={() => syncNow()}
                      disabled={!clientIdPlaceholder || !clientSecretPlaceholder || !tenantIdPlaceholder}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M14.3785 9.17012C13.9791 9.05293 13.5641 9.28243 13.4488 9.6798C12.7723 12.0389 10.6004 13.667 8.14102 13.667C6.06195 13.667 4.17852 12.4707 3.25039 10.667H6.38789C6.80195 10.667 7.13789 10.3311 7.13789 9.91699C7.13789 9.50293 6.83164 9.16699 6.41602 9.16699H1.41602C1.00195 9.16699 0.666016 9.50293 0.666016 9.91699V14.917C0.666016 15.3311 1.00195 15.667 1.41602 15.667C1.83008 15.667 2.16602 15.3311 2.16602 14.917V11.7264C3.40977 13.8045 5.67227 15.167 8.14102 15.167C11.2348 15.167 13.9973 13.0948 14.8629 10.1264C15.0066 9.70137 14.7754 9.31387 14.3785 9.17012ZM14.9191 0.666992C14.5066 0.666992 14.1691 1.00293 14.1691 1.41699V4.60762C12.9285 2.52793 10.6629 1.16699 8.16914 1.16699C5.07539 1.16699 2.3107 3.23918 1.44758 6.20762C1.33133 6.60449 1.55977 7.02012 1.95727 7.13574C2.35852 7.2539 2.77258 7.02246 2.88695 6.62605C3.56602 4.29512 5.73789 2.66699 8.16914 2.66699C10.2482 2.66699 12.1316 3.86324 13.0598 5.66699H9.91914C9.50664 5.66699 9.16914 6.00449 9.16914 6.41699C9.16914 6.82949 9.50664 7.16699 9.91914 7.16699H14.9191C15.3332 7.16699 15.6691 6.83105 15.6691 6.41699V1.41699C15.6691 1.00293 15.3348 0.666992 14.9191 0.666992Z"
                          fill="#244C5A"
                        />
                      </svg>
                      <span className="intune-sync-now-button-text">Sync Now</span>
                    </QButton>
                    <QTypography sx={{ ml: 1, color: '#757575' }}>Last synced: {lastSyncTimestamp}</QTypography>
                  </div>
                  <QButton variant="filled" onClick={() => setMode({ ...mode, intune: SettingsMode.EDIT })}>
                    Edit
                  </QButton>
                </>
              ) : (
                <QButton disabled variant="filled">
                  Save
                </QButton>
              )}
            </>
          )}
        </div>
      </QCardContent>
    </StyledQCard>
  );
};

const StyledQCard = styled(QCard)`
  width: 40%;
  min-width: 300px;
  margin: 20px;
  height: 100%;
`;

const StyledQTypography = styled(QTypography)`
  margin: 20px 0;
  display: block;
`;
